import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import { Alert, CircularProgress, Typography } from '@mui/material';
import { Stack } from '@mui/system';

import createMetadata from '@/shared/api/product/createMetadata';
import createProduct from '@/shared/api/product/createProduct';
import getMetadata from '@/shared/api/product/getMetadata';
import CreateNewMetadataDialog from '@/shared/components/dialogs/CreateNewMetadataDialog';
import ModalNotification from '@/shared/components/dialogs/ModalNotification';
import WaveUIProgressDialog from '@/shared/components/dialogs/WaveUIProgressDialog';
import MetadataFormAccordion from '@/shared/components/forms/MetadataField/components/MetadataFormAccordion';
import PageTitle from '@/shared/components/layouts/PageTitle';
import WaveUINotificationBar from '@/shared/components/utilities/WaveUINotificationBar';
import getFormFieldsToSave from '@/shared/components/widgets/metadata/CreateMetadataForm/utils/getFormFieldsToSave';
import { AppPath } from '@/shared/constants/path';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import DialogName from '@/shared/types/dialogName.enum';
import Metadata from '@/shared/types/metadata/index.type';
import Product from '@/shared/types/products/product.type';

import ProductVariantsForm from '../components/ProductVariantsForm';
import parseMetadataError from './utils/parseMetadataError';

const ProductAdd: FC = () => {
  const navigate = useNavigate();
  const redirectLogin = useRedirectLogin();

  const { dialog, onUpdateDialogState } = useModalDialog<unknown, DialogName>();
  const { progressDialog, onUpdateProgressDialog } = useProgressDialog();
  const { notificationBar, onUpdateNotificationBar } = useNotificationBar();

  // const [productReducerState, dispatchProduct] = useReducer(
  //   productReducer,
  //   productInitialState,
  // );
  const [loading, setLoading] = useState(true);
  const [isSaveChangesInProgress, setIsSaveChangesInProgress] = useState(false);
  const [errors, setErrors] = useState<{
    product?: Record<string, unknown>;
    variant?: Record<string, unknown>[];
  }>({
    product: {},
    variant: [],
  });
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const [metadata, setMetadata] = useState<Metadata | null>(null);

  useEffect(() => {
    (async () => {
      const { data, success } = await getMetadata(() => redirectLogin());

      if (success) {
        setLoading(false);

        // dispatchProduct({
        //   type: 'SET_PRODUCT_INITIAL_METADATA',
        //   payload: data,
        // });

        setMetadata(data);
      }
    })();
  }, [redirectLogin]);

  // const onProductCreate = async () => {
  //   const newMetadata = {
  //     ...[
  //       ...productReducerState.productRequiredMetadata,
  //       ...productReducerState.productOptionalMetadata,
  //     ]
  //       .filter((metadata) => metadata.status === 'added_modified')
  //       .reduce(
  //         (acc, item) => ({
  //           ...acc,
  //           [item.metadata_key]: item[item.metadata_key as keyof typeof item],
  //         }),
  //         {},
  //       ),
  //     variants: productReducerState.variants,
  //   };

  //   const productResponse = await createProduct(
  //     parseJSONInObject(newMetadata as JsonObject),
  //     () => redirectLogin(),
  //   );

  //   if (productResponse.success) {
  //     setIsSaveChangesInProgress(false);
  //     navigate(
  //       `${AppPath.Products}/${(productResponse.data as Product).product_id}`,
  //     );
  //   } else {
  //     setIsSaveChangesInProgress(false);

  //     const keys = [
  //       ...productReducerState.productRequiredMetadata,
  //       ...productReducerState.productOptionalMetadata,
  //     ].map((item) => item.metadata_key);

  //     setErrors({ product: parseMetadataError(keys, productResponse.msg) });
  //     setIsErrorModalVisible(true);
  //   }
  // };

  // const onMetadataCreate = async () => {
  //   const newMetadataItems = productReducerState.newMetadata.filter(
  //     (item) => item.status === 'created_modified',
  //   );

  //   const metadataResponse = await createMetadata(
  //     parseJSONInObject(newMetadataItems as JsonObject[]) as Record<
  //       string,
  //       unknown
  //     >[],
  //     () => redirectLogin(),
  //   );

  //   if (metadataResponse.every((item) => item.success)) {
  //     await onProductCreate();
  //   } else {
  //     setIsSaveChangesInProgress(false);

  //     const keys = productReducerState.newMetadata.map(
  //       (item) => item.metadata_key,
  //     );

  //     const variantErrors = [];

  //     for (const item of metadataResponse) {
  //       if ((item.data as { level: string }).level === 'variant') {
  //         variantErrors.push(parseMetadataError(keys, item.msg));
  //       } else {
  //         setErrors({
  //           ...errors,
  //           [(item.data as { level: string }).level]: parseMetadataError(
  //             keys,
  //             item.msg,
  //           ),
  //         });
  //       }
  //     }

  //     setErrors({
  //       ...errors,
  //       variant: variantErrors,
  //     });

  //     setIsErrorModalVisible(true);
  //   }
  // };

  const onCreateNewMetadata = async (
    formToSave: ReturnType<typeof getFormFieldsToSave>[],
  ) => {
    onUpdateDialogState(DialogName.CreateMetadata, { loading: true });
    onUpdateProgressDialog({
      open: true,
      progress: 0,
      title: 'Creating Metadata',
    });

    const response = await createMetadata(formToSave, redirectLogin);

    if (response.every((item) => item.success)) {
      onUpdateDialogState(DialogName.CreateMetadata, {
        loading: false,
        open: false,
      });
      onUpdateNotificationBar({
        open: true,
        message: 'Metadata Created',
        severity: 'success',
      });
      onUpdateProgressDialog({ open: false });
    } else {
      onUpdateDialogState(DialogName.CreateMetadata, {
        loading: false,
        errors: response.map((item) => item.msg),
      });
      onUpdateProgressDialog({ open: false });
    }
  };

  // const onSubmit = async () => {
  //   setIsSaveChangesInProgress(true);

  //   const newMetadataItems = productReducerState.newMetadata.filter(
  //     (item) => item.status === 'created_modified',
  //   );

  //   if (newMetadataItems.length > 0) {
  //     await onMetadataCreate();
  //     return;
  //   }

  //   await onProductCreate();
  // };

  // Todo: remove old functionality provided above and recheck product creation
  const [products, setProducts] = useState<{
    product: Record<string, unknown>;
    details: Record<string, unknown>;
    variants: Record<string, unknown>[];
  }>({
    product: {},
    details: {},
    variants: [],
  });

  const handleProductCreate = async () => {
    setIsSaveChangesInProgress(true);

    const productRequestBody = {
      ...products.product,
      ...products.details,
      variants: products.variants.map((item) => ({
        ...(item.required as Record<string, unknown>),
        ...(item.optional as Record<string, unknown>),
      })),
    };

    const productResponse = await createProduct(
      productRequestBody,
      redirectLogin,
    );

    if (productResponse.success) {
      setIsSaveChangesInProgress(false);
      navigate(
        `${AppPath.Products}/${(productResponse.data as Product).product_id}`,
      );
    } else {
      setIsSaveChangesInProgress(false);

      const keys =
        metadata?.metadata_definitions.map((item) => item.metadata_key) || [];

      setErrors({ product: parseMetadataError(keys, productResponse.msg) });
      setIsErrorModalVisible(true);
    }
  };

  if (loading) {
    return (
      <Stack
        alignItems='center'
        justifyContent='center'
        height='100%'
        width='100%'
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <div>
      <PageTitle
        title='Add Product'
        breadcrumbs={[
          {
            label: 'Products',
            icon: <ShoppingBagIcon sx={{ width: 14, height: 16 }} />,
            to: AppPath.Products,
          },
          {
            label: 'Add Product',
            icon: <ShoppingBagIcon sx={{ width: 14, height: 16 }} />,
          },
        ]}
        actions={[
          {
            label: 'Create Metadata',
            icon: null,
            button: {
              variant: 'outlined',
              color: 'primary',
              onClick: () => {
                onUpdateDialogState(DialogName.CreateMetadata, {
                  open: true,
                  errors: [],
                });
              },
            },
          },
          {
            label: 'Save Changes',
            icon: null,
            button: {
              variant: 'contained',
              color: 'primary',
              startIcon: isSaveChangesInProgress ? (
                <CircularProgress
                  size='small'
                  sx={{ mr: 1, width: 14, height: 14, color: 'white' }}
                />
              ) : null,
              onClick: () => {
                void handleProductCreate();
              },
            },
          },
        ]}
      />

      <Stack
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          alignItems: 'start',
          gap: 2,
        }}
      >
        {metadata && (
          <>
            <MetadataFormAccordion
              accordionProps={{ title: 'Product' }}
              metadata={metadata}
              levelConstraint='PRODUCT'
              fields={{
                required: products.product,
                optional: products.details,
              }}
              getValues={(values) => {
                setProducts((prev) => ({
                  ...prev,
                  product: { ...prev.product, ...values.required },
                  details: { ...prev.details, ...values.optional },
                }));
              }}
            />
            <ProductVariantsForm
              metadata={metadata}
              getVariants={(values) => {
                setProducts((prev) => ({
                  ...prev,
                  variants: values,
                }));
              }}
            />
          </>
        )}
      </Stack>

      <ModalNotification
        open={isErrorModalVisible}
        onClose={() => setIsErrorModalVisible(false)}
        dialogProps={{
          title: 'Product Creation Failed',
          content: (
            <Stack spacing={2}>
              {Object.entries(errors?.product || {}).map(([key, value]) =>
                key === 'level' ? (
                  <Typography key={key} variant='subtitle2'>
                    {value as string}:
                  </Typography>
                ) : (
                  <Alert severity='error' key={key}>
                    {value as string}
                  </Alert>
                ),
              )}

              {errors.variant &&
                errors.variant.map((item) =>
                  Object.entries(item).map(([key, value]) => {
                    return key === 'level' ? (
                      <Typography key={key} variant='subtitle2'>
                        {value as string}:
                      </Typography>
                    ) : (
                      <Alert severity='error' key={key}>
                        {value as string}
                      </Alert>
                    );
                  }),
                )}

              {/* {errors.variant.map((item) => (
                <Alert severity='error' key={item.metadata_key}>
                  {item.msg}
                </Alert>
              ))} */}
            </Stack>
          ),
          actions: [
            {
              label: 'Close',
              color: 'primary',
              variant: 'contained',
              onClick: () => setIsErrorModalVisible(false),
            },
          ],
        }}
      />

      {dialog?.createMetadata?.open && (
        <CreateNewMetadataDialog
          metadata={metadata}
          module='products'
          errors={dialog.createMetadata.errors}
          onClose={() =>
            onUpdateDialogState(DialogName.CreateMetadata, {
              open: false,
            })
          }
          proceed={(formToSave) => {
            void onCreateNewMetadata(formToSave);
          }}
        />
      )}

      <WaveUINotificationBar
        message={notificationBar.message}
        severity={notificationBar.severity}
        open={notificationBar.open}
        close={() => onUpdateNotificationBar({ open: false })}
      />

      <WaveUIProgressDialog
        title={progressDialog?.title}
        progress={progressDialog?.progress || 0}
        open={progressDialog?.open || false}
        onClose={() => onUpdateProgressDialog({ open: false })}
      />
    </div>
  );
};

export default ProductAdd;
